import * as React from "react"
import AttributeExplanation from "@imminently/decisively-ui/AttributeExplanation"
import GraphProvider from "@imminently/decisively-ui/GraphProvider"
import ReleaseProvider from "@imminently/decisively-ui/ReleaseProvider"
import SdkProvider from "@imminently/decisively-ui/SdkProvider"
import { ComponentPage, ComponentSection } from "../components/ComponentPage"

const nodes = new Map<string, any>([
  ["age", { id: "age", entity: "applicant", path: "applicant/0/age", description: "Age", input: 42 }],
  ["income", { id: "income", entity: "applicant", path: "applicant/0/income", description: "Income", input: 84000 }],
  ["eligible", { id: "eligible", entity: "global", description: "Eligible", derived: true }],
  ["premium", { id: "premium", entity: "global", description: "Premium", derived: 1250 }],
])

const graph = {
  nodes: () => Array.from(nodes.keys()),
  node: (id: string) => nodes.get(id),
  _nodes: Object.fromEntries(nodes),
} as any

const release = {
  relationships: [
    {
      id: "applicant-rel",
      source: "global",
      target: "applicant",
      name: "applicant",
      text: "for each applicant",
      containment: true,
    },
  ],
} as any

const translations: Record<string, string> = {
  "explanation.input_node": "Input node",
  "explanation.decison_table_heading": "Decision table",
  "explanation.decision_table_value_of": "Value",
  "explanation.decision_table_conditions_met": "Conditions met",
  "explanation.beginning_of_time": "Beginning of Time",
  "explanation.end_of_time": "End of Time",
  "explanation.uncertain": "Uncertain",
}

const t = (key: string) => translations[key] ?? key

const conditionalNode = {
  id: "eligible",
  description: "Eligible",
  entity: "global",
  derived: true,
  conditions: [
    {
      type: "and",
      elements: [
        {
          type: "greater-than-equals",
          elements: [{ type: "attribute", attributeId: "age" }, { type: "value", value: 18 }],
        },
        {
          type: "greater-than",
          elements: [{ type: "attribute", attributeId: "income" }, { type: "value", value: 50000 }],
        },
      ],
    },
  ],
}

const assignmentNode = {
  id: "premium",
  description: "Premium",
  entity: "global",
  derived: 1250,
  value: [
    {
      type: "plus",
      elements: [{ type: "value", value: 1000 }, { type: "value", value: 250 }],
    },
  ],
}

const tableNode = {
  id: "risk-band",
  description: "Risk band",
  entity: "global",
  derived: "standard",
  value: [
    { type: "value", value: "preferred" },
    { type: "value", value: "standard" },
    { type: "value", value: "refer" },
    { type: "value", value: null },
  ],
  rows: [
    [
      {
        type: "greater-than-equals",
        elements: [{ type: "attribute", attributeId: "income" }, { type: "value", value: 100000 }],
      },
    ],
    [
      {
        type: "greater-than",
        elements: [{ type: "attribute", attributeId: "income" }, { type: "value", value: 50000 }],
      },
    ],
    [
      {
        type: "greater-than",
        elements: [{ type: "attribute", attributeId: "income" }, { type: "value", value: 0 }],
      },
    ],
    [{ type: "empty" }],
  ],
}

const existsNode = {
  id: "has-adult-applicant",
  description: "Has adult applicant",
  entity: "global",
  derived: true,
  conditions: [
    {
      type: "exists-scope",
      relationshipId: "applicant-rel",
      elements: [
        {
          type: "greater-than-equals",
          elements: [{ type: "attribute", attributeId: "age" }, { type: "value", value: 18 }],
        },
      ],
    },
  ],
}

const ExamplePanel = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-md border border-border bg-card p-4">
    <div className="mb-4 text-xs font-medium uppercase text-muted-foreground">{title}</div>
    {children}
  </div>
)

export function RuleExplanationPage() {
  return (
    <SdkProvider t={t}>
      <ReleaseProvider release={release}>
        <GraphProvider graph={graph}>
          <ComponentPage
            title="Rule Explanation"
            description="Rule and decision-table explanations for graph attributes, based on the monitor SDK RuleDescription examples."
          >
            <ComponentSection title="Rules">
              <div className="grid gap-4">
                <ExamplePanel title="Conditional rule">
                  <AttributeExplanation node={conditionalNode as any} selectedNode={conditionalNode as any} />
                </ExamplePanel>
                <ExamplePanel title="Simple assignment">
                  <AttributeExplanation node={assignmentNode as any} selectedNode={assignmentNode as any} />
                </ExamplePanel>
                <ExamplePanel title="Exists scope">
                  <AttributeExplanation node={existsNode as any} selectedNode={existsNode as any} />
                </ExamplePanel>
              </div>
            </ComponentSection>

            <ComponentSection title="Decision table">
              <ExamplePanel title="Table explanation">
                <AttributeExplanation node={tableNode as any} selectedNode={tableNode as any} />
              </ExamplePanel>
            </ComponentSection>
          </ComponentPage>
        </GraphProvider>
      </ReleaseProvider>
    </SdkProvider>
  )
}
